export interface Reference {
  name: string;
  address?: string;
  phone: string;
  relation: string;
}

export interface Application {
  numOfApplicants: number | null;
  type: number;
  loanType: number;
  /**
   * Numeric collateral code paired with `loanType`, set on the loan-selection
   * step when the chosen tile has a sub-step (Powersports, Tractor/Trailer/
   * Implement). `undefined` for non-vehicle loans.
   */
  collateralCode?: number;
  finances: unknown[]; // You can replace this with a proper interface if needed
  details: Details;
  options: {
    creditLimit: number | null;
    loanAmount: string;
    term: string;
    termInMonths: string;
    termDuration: string;
    segDivision: string;
    shareSecured: {
      maturity: unknown;
      maturityInMonths: unknown;
      maturityDuration: string;
    };
    payment: {
      paymentFrequency: string;
      paymentMethod: string;
      dueDay1: number;
      dueDay2: number;
      firstPaymentDate: string;
      autoTransferAccount: string;
      autoTransferShare: string;
    };
    interestRate: {
      rate: string;
      rateMatch: boolean;
      rateMatchRate: number;
      promotion: boolean;
      promotionRate: number;
    };
    debtProtection: {
      lifePlus: boolean;
      disability: boolean;
      unemployment: boolean;
    };
    other: unknown[];
  };
  references: {
    one: Reference;
    two: Reference;
  };
  status: {
    approval: {
      amount: number;
      code: number;
      date: string;
      debtToIncome: number;
      loanToValue: number;
      payment: number;
      rate: number;
    };
    decisionCode: number;
    decisionDate: string;
    pendingDate: string;
    cancellationDate: string;
    denialDate: string;
    denialReasons: unknown[];
  };
  exceptions: {
    loanToValue: boolean;
    interestRate: boolean;
    maturity: boolean;
    shareSecuredAmount: boolean;
    shareSecuredTerm: boolean;
    shareSecuredMaturityTerm: boolean;
    lineOfCreditLimit: boolean;
    creditCardLimit: boolean;
  };
  creditWarnings: {
    bankruptcies: unknown[];
    collections: unknown[];
    fraud: unknown[];
  };
}

export interface Details {
  collaterals: AppCollateral[];
  heloc?: HELOC;
  secured: Secured;
}

export interface AppCollateral {
  filingFee: number;
  titleFee: number;
  make: string;
  mileage: string;
  model: string;
  value: number;
  vin: string;
  year: string;
}

export interface HELOC {
  legalDescription: string;
  universalLoanId: string;
  /**
   * NMLS Registry ID for the mortgage loan originator.
   *
   * Field Number: 254
   * Mnemonic: HMDANMLSRID
   */
  nmlsIdentifer: string;
  /**
   * Purpose of the covered loan or application.
   *
   * @property {number} loanPurpose
   * @value 1 - Home Purchase: Loan to purchase a home
   * @value 2 - Home Improvement: Loan for home improvements
   * @value 4 - Other Purpose: Loan not related to the home
   * @value 5 - Not Applicable: Purpose not relevant to this loan
   * @value 31 - Refinancing: Loan to refinance the mortgage
   * @value 32 - Cash-Out Refinancing: Refinance mortgage with cash out
   *
   * Field Number: 214
   * Mnemonic: HMDALOANPURPOSE
   */
  purpose: 1 | 2 | 4 | 5 | 31 | 32;
  /**
   * Indicates if the loan/application involved a preapproval request.
   * Note: HMDA does not allow "Not Applicable" as a response.
   *
   * @property {number} preapproval
   * @value 0 - Not Applicable (must be changed to another value)
   * @value 1 - Preapproval Requested
   * @value 2 - Preapproval Not Requested
   *
   * Field Number: 215
   * Mnemonic: HMDAPREAPPROVAL
   */
  preapproval: 0 | 1 | 2;
  /**
   * Type of property for the loan.
   *
   * @property {number} propertyType
   * @value 0 - Single Family Home
   * @value 1 - Condominium
   * @value 2 - Townhouse
   * @value 3 - Other (requires propertyTypeOther description)
   */
  propertyType: 0 | 1 | 2 | 3;
  /**
   * Additional description for property type when "Other" (3) is selected.
   */
  propertyTypeOther?: string;
  /**
   * Indicates how the system stores the property address information.
   * Used for IRS Form 1098 reporting.
   *
   * @property {number} propertyAddressType
   * @value 0 - Same as Primary Member Address
   * @value 1 - Property Address: Different than primary member's address
   * @value 2 - Property Description: No property address available
   *
   * Field Number: 210
   * Mnemonic: PROPERTYADDRESSTYPE
   */
  propertyAddressType: 0 | 1 | 2;
  /**
   * Construction method for the dwelling.
   * Note: HMDA does not allow "Not Applicable" as a response.
   *
   * @property {number} constructionMethod
   * @value 0 - Not Applicable (must be changed to another value)
   * @value 1 - Site-Built: Constructed on site
   * @value 2 - Manufactured Home: Factory-built and moved to site
   *
   * Field Number: 216
   * Mnemonic: HMDACONSTRUCTIONMETHOD
   */
  constructionMethod: 0 | 1 | 2;
  /**
   * Specifies the occupancy status of the property for this loan.
   *
   * @property {number} occupancyType
   * @value 0 - Not Applicable: Property is not a principal residence, secondary
   *           residence, or investment property
   * @value 1 - Principal Residence: Property the borrower plans to occupy as
   *           principal residence
   * @value 2 - Second Residence: Property is a second home or vacation home
   * @value 3 - Investment Property with Rental Income: Property to generate
   *           rental income
   *
   * Note: This field must be populated to avoid an exception when generating
   * IRS Form 1098.
   *
   * Field Number: 384
   * Mnemonic: OCCUPANCYTYPE
   */
  occupancyType: 0 | 1 | 2 | 3;
  /**
   * Action taken on the covered loan or application.
   * Note: HMDA does not allow "Not Applicable" as a response.
   *
   * @property {number} actionTaken
   * @value 0 - Not Applicable (must be changed to another value)
   * @value 1 - Loan Originated
   * @value 2 - Application approved but not accepted
   * @value 3 - Application Denied
   * @value 4 - Application Withdrawn by Applicant
   * @value 5 - File Closed for Incompleteness
   * @value 6 - Purchased Loan
   * @value 7 - Preapproval Request Denied
   * @value 8 - Preapproval request approved but not accepted
   *
   * Field Number: 232
   * Mnemonic: HMDAACTIONTAKEN
   */
  actionTaken: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  /**
   * Date when action was taken on the loan/application.
   * Format: MM/YY/DDDD
   *
   * Field Number: 233
   * Mnemonic: HMDAACTIONTAKENDATE
   */
  actionTakenDate: string;
  /**
   * Lien status of the property securing the covered loan.
   * Note: HMDA does not allow "Not Applicable" as a response.
   *
   * @property {number} lienStatus
   * @value 0 - Not Applicable (must be changed to another value)
   * @value 1 - Secured by a first lien
   * @value 2 - Secured by a subordinate lien
   *
   * Field Number: 278
   * Mnemonic: HMDALIENSTATUS
   */
  lienStatus: 0 | 1 | 2;
  firstMortgageBalance?: number | null;
  parcelNumbers: string[];
  deedOfTrust: {
    borrowers: string;
    individualBorrowers: string[];
    nameStatus:
      | null
      | 'Husband/Wife'
      | 'Wife/Husband'
      | 'Trust'
      | 'Trustees'
      | 'Single'
      | 'Unmarried Individual'
      | 'Other';
    nameStatusOther?: string | null;
  };
  property: {
    street: string;
    city: string;
    state: string;
    zip: string;
    value: string;
    county: string;
    countyName: string;
    censusTract: string;
    inFloodZone: boolean;
  };
  rescissionDate: string;
  valuationMethod: 'HVE' | 'Appraisal' | null;
  appraisalDate: string;
}

export interface Secured {
  account: null;
  id: null;
}

export const defaultApplication: Application = {
  numOfApplicants: null,
  type: 0,
  loanType: 0,
  collateralCode: undefined,
  finances: [],
  details: {
    collaterals: [
      {
        filingFee: 0,
        titleFee: 0,
        make: '',
        mileage: '',
        model: '',
        value: 0,
        vin: '',
        year: '',
      },
    ],
    secured: {
      account: null,
      id: null,
    },
  },
  options: {
    creditLimit: null,
    loanAmount: '',
    term: '0',
    termInMonths: '',
    termDuration: '',
    segDivision: '',
    shareSecured: {
      maturity: undefined,
      maturityInMonths: undefined,
      maturityDuration: '',
    },
    payment: {
      paymentFrequency: '',
      paymentMethod: '',
      dueDay1: 0,
      dueDay2: 0,
      firstPaymentDate: '',
      autoTransferAccount: '',
      autoTransferShare: '',
    },
    interestRate: {
      rate: '',
      rateMatch: false,
      rateMatchRate: 0,
      promotion: false,
      promotionRate: 0,
    },
    debtProtection: {
      lifePlus: false,
      disability: false,
      unemployment: false,
    },
    other: [],
  },
  references: {
    one: {
      name: '',
      phone: '',
      relation: '',
    },
    two: {
      name: '',
      address: '',
      phone: '',
      relation: '',
    },
  },
  status: {
    approval: {
      amount: 0,
      code: 0,
      date: '',
      debtToIncome: 0,
      loanToValue: 0,
      payment: 0,
      rate: 0,
    },
    decisionCode: 0,
    decisionDate: '',
    pendingDate: '',
    cancellationDate: '',
    denialDate: '',
    denialReasons: [],
  },
  exceptions: {
    loanToValue: false,
    interestRate: false,
    maturity: false,
    shareSecuredAmount: false,
    shareSecuredTerm: false,
    shareSecuredMaturityTerm: false,
    lineOfCreditLimit: false,
    creditCardLimit: false,
  },
  creditWarnings: {
    bankruptcies: [],
    collections: [],
    fraud: [],
  },
};
